import React, { useEffect, useRef } from 'react';
import * as Sentry from "@sentry/react";

interface BookingWidgetProps {
    widgetCode: string;
    title?: string;
}

declare global {
    interface Window {
        SimplybookWidget?: unknown;
        __simplybook_widget_initialized?: boolean;
    }
}

const BookingWidget: React.FC<BookingWidgetProps> = ({ widgetCode, title }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const initializedRef = useRef(false);

    useEffect(() => {
        if (initializedRef.current) {
            return;
        }
        initializedRef.current = true;

        let intervalId: number | undefined;

        try {
            const widgetHtml = decodeURIComponent(widgetCode);
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = widgetHtml;

            // 1. insert script src
            const scriptSrc = tempDiv.querySelector('script[src]') as HTMLScriptElement;
            if (scriptSrc && !document.querySelector(`script[src="${scriptSrc.src}"]`)) {
                const newScript = document.createElement('script');
                newScript.src = scriptSrc.src;
                newScript.type = scriptSrc.type;
                document.body.appendChild(newScript);
            }

            // 2. insert mount point (optional, depends on widget requirements)
            if (containerRef.current) {
                containerRef.current.innerHTML = '';
            }

            // 3. wait for widget.js to load then execute inline script
            const inlineScript = tempDiv.querySelector('script:not([src])');
            if (inlineScript) {
                const savedTitle = title || document.title;
                const runInline = () => {
                    if (!containerRef.current) return;
                    try {
                        eval((inlineScript as HTMLScriptElement).text);
                    } catch (e) {
                        Sentry.captureException(e);
                    }
                    // SimplyBook widget.js may overwrite document.title, restore it
                    document.title = savedTitle;
                };

                if (window.SimplybookWidget) {
                    runInline();
                } else {
                    intervalId = window.setInterval(() => {
                        if (window.SimplybookWidget) {
                            clearInterval(intervalId);
                            runInline();
                        }
                    }, 100);
                }
            }
        } catch (error) {
            console.error('Error initializing widget:', error);
            Sentry.captureException(error);
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [widgetCode, title]);

    return <div ref={containerRef} />;
};

export default BookingWidget; 